<template>
  <div class="energy-dashboard" :class="{ 'ed--dark': darkMode }">
    <!-- ═══════════════════ HEADER ═══════════════════ -->
    <div class="ed-header d-flex align-items-center justify-content-between mb-3">
      <div class="d-flex align-items-center gap-2">
        <div class="ed-header__icon">
          <i class="bi bi-lightning text-white"></i>
        </div>
        <div>
          <h4 class="mb-0">Energy Dashboard</h4>
          <small class="text-muted">
            {{ installations.length }} installation{{ installations.length !== 1 ? 's' : '' }}
            <template v-if="hasRun"> &middot; Last updated {{ lastUpdateLabel }}</template>
          </small>
        </div>
      </div>
      <div class="d-flex align-items-center gap-2">
        <!-- Forecast period -->
        <select v-model="forecastPeriod" class="form-select form-select-sm" style="width:auto">
          <option value="next3days">3 Days</option>
          <option value="next7days">7 Days</option>
          <option value="next14days">14 Days</option>
        </select>
        <!-- Unit toggle -->
        <div class="btn-group btn-group-sm">
          <button class="btn" :class="unitGroup === 'metric' ? 'btn-primary' : 'btn-outline-primary'" @click="setUnitGroup('metric')">°C</button>
          <button class="btn" :class="unitGroup === 'us' ? 'btn-primary' : 'btn-outline-primary'" @click="setUnitGroup('us')">°F</button>
        </div>
        <!-- Dark mode -->
        <button class="btn btn-sm btn-outline-secondary" @click="darkMode = !darkMode" :title="darkMode ? 'Light mode' : 'Dark mode'">
          <i class="bi" :class="darkMode ? 'bi-sun' : 'bi-moon'"></i>
        </button>
        <!-- Refresh -->
        <button class="btn btn-primary btn-sm" :disabled="isLoading || installations.length === 0" @click="refreshData">
          <i class="bi bi-arrow-clockwise me-1" :class="{ 'spin-icon': isLoading }"></i>
          {{ isLoading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <!-- ═══════════════════ SIGN-IN GATE ═══════════════════ -->
    <div v-if="!hasSession" class="alert alert-warning d-flex align-items-center">
      <i class="bi bi-lock me-3 fs-4"></i>
      <div class="flex-grow-1">
        <strong>Sign in required</strong>
        <p class="mb-0 small">Please sign in to access the Energy Dashboard.</p>
      </div>
      <a class="btn btn-primary btn-sm" href="#" onclick="window.dialogManager?.showSignIn(); return false;">
        <i class="bi bi-box-arrow-in-right me-1"></i>Sign In
      </a>
    </div>

    <template v-else>
      <!-- Progress bar -->
      <div v-if="isLoading" class="progress mb-3" style="height:4px">
        <div class="progress-bar progress-bar-striped progress-bar-animated" :style="{ width: loadingProgress + '%' }"></div>
      </div>

      <!-- ═══════════════════ DASHBOARD TABS ═══════════════════ -->
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item" v-for="tab in tabs" :key="tab.id">
          <a class="nav-link" :class="{ active: activeTab === tab.id }" href="#" @click.prevent="activeTab = tab.id">
            <i :class="tab.icon" class="me-1"></i>{{ tab.label }}
            <span v-if="tab.id === 'dashboard' && attentionInstallations.length" class="badge bg-danger ms-1">{{ attentionInstallations.length }}</span>
          </a>
        </li>
      </ul>

      <!-- ════════════════ TAB: DASHBOARD ════════════════ -->
      <div v-show="activeTab === 'dashboard'">
        <!-- Summary Cards -->
        <div v-if="overallSummary" class="row g-3 mb-4">
          <div class="col-6 col-md-3">
            <div class="ed-stat-card">
              <div class="ed-stat-card__icon"><i class="bi bi-speedometer2" :style="{ color: riskColor(overallSummary.maxRiskLevel) }"></i></div>
              <div class="ed-stat-card__label">Max Risk</div>
              <div class="ed-stat-card__value" :style="{ color: riskColor(overallSummary.maxRiskLevel) }">{{ overallSummary.maxRisk }}/100</div>
              <div class="ed-stat-card__sub text-capitalize">{{ overallSummary.maxRiskLevel }}</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="ed-stat-card">
              <div class="ed-stat-card__icon"><i class="bi bi-lightning text-warning"></i></div>
              <div class="ed-stat-card__label">Current Output</div>
              <div class="ed-stat-card__value">{{ formatPower(overallSummary.totalGenKW) }}</div>
              <div class="ed-stat-card__sub">Combined</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="ed-stat-card">
              <div class="ed-stat-card__icon"><i class="bi bi-lightning text-success"></i></div>
              <div class="ed-stat-card__label">Avg Daily</div>
              <div class="ed-stat-card__value">{{ formatEnergy(overallSummary.totalDailyKWh) }}</div>
              <div class="ed-stat-card__sub">Per day avg</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="ed-stat-card">
              <div class="ed-stat-card__icon"><i class="bi bi-building text-info"></i></div>
              <div class="ed-stat-card__label">Installations</div>
              <div class="ed-stat-card__value">{{ installations.length }}</div>
              <div class="ed-stat-card__sub">{{ overallSummary.solarCount }} solar, {{ overallSummary.windCount }} wind</div>
            </div>
          </div>
        </div>

        <!-- Installations Requiring Attention -->
        <div v-if="attentionInstallations.length" class="mb-4">
          <h6 class="text-uppercase text-muted small fw-bold mb-2"><i class="bi bi-exclamation-triangle me-1"></i>Installations Requiring Attention</h6>
          <div class="row g-3">
            <div v-for="inst in (showAllAttention ? attentionInstallations : attentionInstallations.slice(0, 2))" :key="inst.id" class="col-md-6">
              <div class="ed-attention-card" :style="{ borderLeftColor: riskColor(inst.riskLevel) }">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 class="mb-1">
                      <i :class="inst.type === 'solar' ? 'bi-sun' : 'bi-wind'" class="bi me-1"></i>
                      {{ inst.name }}
                    </h6>
                    <span class="badge" :class="riskBadgeClass(inst.riskLevel)">{{ inst.riskLevel }} ({{ inst.riskScore }})</span>
                  </div>
                  <button class="btn btn-outline-primary btn-sm" @click="selectInstallation(inst)">
                    <i class="bi bi-arrow-right"></i>
                  </button>
                </div>
                <div class="mt-2 small text-muted">
                  {{ inst.lat.toFixed(4) }}, {{ inst.lon.toFixed(4) }}
                  <span v-if="inst.alerts.length" class="ms-2 text-danger">
                    <i class="bi bi-exclamation-triangle me-1"></i>{{ inst.alerts.length }} alert{{ inst.alerts.length > 1 ? 's' : '' }}
                  </span>
                </div>
                <div v-if="inst.riskFactors.length" class="mt-2">
                  <span v-for="f in inst.riskFactors.filter(f => f.score >= 30).slice(0, 3)" :key="f.name"
                        class="badge me-1" :class="riskBadgeClass(f.level)">{{ f.name }}: {{ f.score }}</span>
                </div>
              </div>
            </div>
          </div>
          <button v-if="attentionInstallations.length > 2" class="btn btn-link btn-sm" @click="showAllAttention = !showAllAttention">
            {{ showAllAttention ? 'Show Less' : `Show All (${attentionInstallations.length})` }}
          </button>
        </div>

        <!-- Installation Map -->
        <div class="card mb-4">
          <div class="card-header py-2 d-flex justify-content-between align-items-center">
            <h6 class="mb-0"><i class="bi bi-geo me-2"></i>Installation Map</h6>
            <select v-if="installations.length" class="form-select form-select-sm" style="width:auto" @change="flyToInstallation($event.target.value)">
              <option value="">View All Installations</option>
              <option v-for="inst in installations" :key="inst.id" :value="inst.id">{{ inst.name }}</option>
            </select>
          </div>
          <div class="card-body p-0">
            <div ref="mapContainer" class="ed-map-container"></div>
          </div>
        </div>

        <!-- All Installations Grid -->
        <div class="card">
          <div class="card-header py-2">
            <h6 class="mb-0"><i class="bi bi-grid me-2"></i>All Installations</h6>
          </div>
          <div class="card-body p-0">
            <div v-if="!installations.length" class="text-center text-muted py-5">
              <i class="bi bi-plus-circle fs-1 d-block mb-2"></i>
              <p>No installations configured. Go to <strong>Solar Panels</strong> or <strong>Wind Turbines</strong> to add installations.</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-hover mb-0 align-middle">
                <thead><tr>
                  <th>Name</th><th>Type</th><th>Location</th>
                  <th>Risk</th><th>Current Output</th><th>Daily Avg</th><th>Cap. Factor</th><th></th>
                </tr></thead>
                <tbody>
                  <tr v-for="inst in installations" :key="inst.id" class="cursor-pointer" @click="selectInstallation(inst)">
                    <td class="fw-semibold">{{ inst.name }}</td>
                    <td><i :class="inst.type === 'solar' ? 'bi-sun text-warning' : 'bi-wind text-info'" class="bi me-1"></i>{{ inst.type === 'solar' ? 'Solar' : 'Wind' }}</td>
                    <td class="small text-muted">{{ inst.lat.toFixed(2) }}, {{ inst.lon.toFixed(2) }}</td>
                    <td>
                      <span v-if="inst.riskScore != null" class="badge" :class="riskBadgeClass(inst.riskLevel)">{{ inst.riskScore }}</span>
                      <span v-else class="text-muted">–</span>
                    </td>
                    <td>{{ inst.status === 'ok' ? formatPower(inst.currentGenKW) : '–' }}</td>
                    <td>{{ inst.status === 'ok' ? formatEnergy(inst.dailyGenKWh) : '–' }}</td>
                    <td>{{ inst.status === 'ok' ? inst.capacityFactor + '%' : '–' }}</td>
                    <td><button class="btn btn-sm btn-outline-primary" @click.stop="selectInstallation(inst)"><i class="bi bi-arrow-right"></i></button></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- ════════════════ TAB: SOLAR PANELS ════════════════ -->
      <div v-show="activeTab === 'solar'">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0"><i class="bi bi-sun me-2 text-warning"></i>Solar Panel Installations</h5>
          <button class="btn btn-primary btn-sm" @click="openAddModal('solar')">
            <i class="bi bi-plus me-1"></i>Add Solar Panel
          </button>
        </div>

        <div v-if="!solarInstallations.length" class="text-center text-muted py-5">
          <i class="bi bi-sun fs-1 d-block mb-2 text-warning"></i>
          <p>No solar installations configured. Click <strong>Add Solar Panel</strong> to get started.</p>
        </div>

        <div v-else class="row g-3">
          <div v-for="inst in solarInstallations" :key="inst.id" class="col-md-6 col-xl-4">
            <div class="ed-install-card" :class="{ 'ed-install-card--selected': selectedInstallation?.id === inst.id }"
                 @click="selectInstallation(inst)">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h6 class="mb-0"><i class="bi bi-sun me-1 text-warning"></i>{{ inst.name }}</h6>
                  <small class="text-muted">{{ inst.lat.toFixed(4) }}, {{ inst.lon.toFixed(4) }}</small>
                </div>
                <div class="d-flex gap-1">
                  <span v-if="inst.riskScore != null" class="badge" :class="riskBadgeClass(inst.riskLevel)">
                    Risk: {{ inst.riskScore }}
                  </span>
                  <button class="btn btn-sm btn-outline-secondary" @click.stop="openEditModal(inst)"><i class="bi bi-pencil"></i></button>
                  <button class="btn btn-sm btn-outline-danger" @click.stop="confirmRemove(inst)"><i class="bi bi-trash"></i></button>
                </div>
              </div>
              <div class="row g-2 small">
                <div class="col-6"><span class="text-muted">Area:</span> {{ inst.areaSqM }} m²</div>
                <div class="col-6"><span class="text-muted">Peak Power:</span> {{ (inst.peakPowerW / 1000).toFixed(1) }} kW</div>
                <div class="col-6"><span class="text-muted">Efficiency:</span> {{ (inst.panelEfficiency * 100).toFixed(1) }}%</div>
                <div class="col-6"><span class="text-muted">Tilt/Azimuth:</span> {{ inst.tiltAngle }}° / {{ inst.azimuth }}°</div>
              </div>
              <div v-if="inst.status === 'ok'" class="mt-2 pt-2 border-top">
                <div class="row g-2 small">
                  <div class="col-4 text-center">
                    <div class="fw-bold">{{ formatPower(inst.currentGenKW) }}</div>
                    <div class="text-muted">Current</div>
                  </div>
                  <div class="col-4 text-center">
                    <div class="fw-bold">{{ formatPower(inst.peakGenKW) }}</div>
                    <div class="text-muted">Peak</div>
                  </div>
                  <div class="col-4 text-center">
                    <div class="fw-bold">{{ formatEnergy(inst.dailyGenKWh) }}</div>
                    <div class="text-muted">Daily Avg</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ════════════════ TAB: WIND TURBINES ════════════════ -->
      <div v-show="activeTab === 'wind'">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0"><i class="bi bi-wind me-2 text-info"></i>Wind Turbine Installations</h5>
          <button class="btn btn-primary btn-sm" @click="openAddModal('wind')">
            <i class="bi bi-plus me-1"></i>Add Wind Turbine
          </button>
        </div>

        <div v-if="!windInstallations.length" class="text-center text-muted py-5">
          <i class="bi bi-wind fs-1 d-block mb-2 text-info"></i>
          <p>No wind installations configured. Click <strong>Add Wind Turbine</strong> to get started.</p>
        </div>

        <div v-else class="row g-3">
          <div v-for="inst in windInstallations" :key="inst.id" class="col-md-6 col-xl-4">
            <div class="ed-install-card" :class="{ 'ed-install-card--selected': selectedInstallation?.id === inst.id }"
                 @click="selectInstallation(inst)">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h6 class="mb-0"><i class="bi bi-wind me-1 text-info"></i>{{ inst.name }}</h6>
                  <small class="text-muted">{{ inst.lat.toFixed(4) }}, {{ inst.lon.toFixed(4) }}</small>
                </div>
                <div class="d-flex gap-1">
                  <span v-if="inst.riskScore != null" class="badge" :class="riskBadgeClass(inst.riskLevel)">
                    Risk: {{ inst.riskScore }}
                  </span>
                  <button class="btn btn-sm btn-outline-secondary" @click.stop="openEditModal(inst)"><i class="bi bi-pencil"></i></button>
                  <button class="btn btn-sm btn-outline-danger" @click.stop="confirmRemove(inst)"><i class="bi bi-trash"></i></button>
                </div>
              </div>
              <div class="row g-2 small">
                <div class="col-6"><span class="text-muted">Hub Height:</span> {{ inst.hubHeightM }} m</div>
                <div class="col-6"><span class="text-muted">Rated Power:</span> {{ inst.ratedPowerKW }} kW</div>
              </div>
              <div v-if="inst.status === 'ok'" class="mt-2 pt-2 border-top">
                <div class="row g-2 small">
                  <div class="col-4 text-center">
                    <div class="fw-bold">{{ formatPower(inst.currentGenKW) }}</div>
                    <div class="text-muted">Current</div>
                  </div>
                  <div class="col-4 text-center">
                    <div class="fw-bold">{{ formatPower(inst.peakGenKW) }}</div>
                    <div class="text-muted">Peak</div>
                  </div>
                  <div class="col-4 text-center">
                    <div class="fw-bold">{{ formatEnergy(inst.dailyGenKWh) }}</div>
                    <div class="text-muted">Daily Avg</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ════════════════ TAB: DETAIL VIEW ════════════════ -->
      <div v-show="activeTab === 'detail'">
        <div v-if="!selectedInstallation" class="text-center text-muted py-5">
          <i class="bi bi-geo fs-1 d-block mb-2"></i>
          <p>Select an installation from the dashboard, solar, or wind tab to view detailed analysis.</p>
        </div>
        <template v-else>
          <!-- Detail Header -->
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 class="mb-0">
                <i :class="selectedInstallation.type === 'solar' ? 'bi-sun text-warning' : 'bi-wind text-info'" class="bi me-1"></i>
                {{ selectedInstallation.name }}
              </h5>
              <small class="text-muted">{{ selectedInstallation.lat.toFixed(4) }}, {{ selectedInstallation.lon.toFixed(4) }}</small>
            </div>
            <div class="d-flex gap-2">
              <ul class="nav nav-pills nav-pills-sm">
                <li class="nav-item"><a class="nav-link py-1 px-2" :class="{ active: detailView === 'risk' }" href="#" @click.prevent="detailView = 'risk'">Risk</a></li>
                <li class="nav-item"><a class="nav-link py-1 px-2" :class="{ active: detailView === 'generation' }" href="#" @click.prevent="detailView = 'generation'">Generation</a></li>
                <li class="nav-item"><a class="nav-link py-1 px-2" :class="{ active: detailView === 'weather' }" href="#" @click.prevent="detailView = 'weather'">Weather</a></li>
                <li class="nav-item" v-if="selectedInstallation.type === 'wind'"><a class="nav-link py-1 px-2" :class="{ active: detailView === 'windprofile' }" href="#" @click.prevent="detailView = 'windprofile'">Wind Profile</a></li>
                <li class="nav-item" v-if="selectedInstallation.type === 'solar'"><a class="nav-link py-1 px-2" :class="{ active: detailView === 'irradiance' }" href="#" @click.prevent="detailView = 'irradiance'">Irradiance</a></li>
              </ul>
            </div>
          </div>

          <!-- Detail: Risk Assessment -->
          <div v-if="detailView === 'risk'">
            <div class="row g-3 mb-4">
              <div class="col-md-4">
                <div class="ed-risk-gauge text-center p-4" :style="{ borderColor: riskColor(selectedInstallation.riskLevel) }">
                  <div class="ed-risk-gauge__score" :style="{ color: riskColor(selectedInstallation.riskLevel) }">
                    {{ selectedInstallation.riskScore ?? '–' }}
                  </div>
                  <div class="ed-risk-gauge__label text-capitalize fw-bold">{{ selectedInstallation.riskLevel || 'Unknown' }}</div>
                  <div class="small text-muted mt-2">Combined Risk Score</div>
                </div>
              </div>
              <div class="col-md-8">
                <h6 class="text-uppercase text-muted small fw-bold mb-2">Risk Factors</h6>
                <table class="table table-sm table-bordered">
                  <thead><tr><th>Factor</th><th>Weight</th><th>Score</th><th>Level</th></tr></thead>
                  <tbody>
                    <tr v-for="f in selectedInstallation.riskFactors" :key="f.name">
                      <td>{{ f.name }}</td>
                      <td class="text-capitalize"><span class="badge bg-secondary">{{ f.weight }}</span></td>
                      <td>
                        <div class="d-flex align-items-center gap-2">
                          <div class="progress flex-grow-1" style="height:8px">
                            <div class="progress-bar" :style="{ width: f.score + '%', backgroundColor: riskColor(f.level) }"></div>
                          </div>
                          <span class="small fw-bold" style="min-width:28px">{{ f.score }}</span>
                        </div>
                      </td>
                      <td><span class="badge" :class="riskBadgeClass(f.level)">{{ f.level }}</span></td>
                    </tr>
                  </tbody>
                </table>
                <!-- Alerts -->
                <div v-if="selectedInstallation.alerts?.length" class="mt-3">
                  <h6 class="text-uppercase text-muted small fw-bold mb-2"><i class="bi bi-exclamation-triangle me-1 text-danger"></i>Weather Alerts</h6>
                  <div v-for="(alert, idx) in selectedInstallation.alerts" :key="idx" class="alert alert-danger py-2 small">
                    <strong>{{ alert.event || 'Alert' }}</strong>
                    <span v-if="alert.headline"> &mdash; {{ alert.headline }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Detail: Generation -->
          <div v-if="detailView === 'generation'">
            <!-- Generation summary cards -->
            <div class="row g-3 mb-4">
              <div class="col-6 col-md-3">
                <div class="ed-stat-card">
                  <div class="ed-stat-card__label">Current Output</div>
                  <div class="ed-stat-card__value text-primary">{{ formatPower(selectedInstallation.currentGenKW) }}</div>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="ed-stat-card">
                  <div class="ed-stat-card__label">Peak Power</div>
                  <div class="ed-stat-card__value text-success">{{ formatPower(selectedInstallation.peakGenKW) }}</div>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="ed-stat-card">
                  <div class="ed-stat-card__label">Daily Avg</div>
                  <div class="ed-stat-card__value text-warning">{{ formatEnergy(selectedInstallation.dailyGenKWh) }}</div>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="ed-stat-card">
                  <div class="ed-stat-card__label">Capacity Factor</div>
                  <div class="ed-stat-card__value text-info">{{ selectedInstallation.capacityFactor }}%</div>
                </div>
              </div>
            </div>
            <!-- Hourly generation chart -->
            <div class="card mb-3">
              <div class="card-header py-2"><h6 class="mb-0">Hourly Generation Forecast</h6></div>
              <div class="card-body p-2">
                <div ref="genChartContainer" class="ed-chart-container"></div>
              </div>
            </div>
            <!-- Daily generation table -->
            <div class="card">
              <div class="card-header py-2"><h6 class="mb-0">Daily Generation Summary</h6></div>
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-sm table-hover mb-0">
                    <thead><tr>
                      <th>Date</th><th>Conditions</th><th>Total (kWh)</th><th>Peak (kW)</th>
                      <template v-if="selectedInstallation.type === 'solar'"><th>Temp</th></template>
                      <template v-else><th>Wind</th></template>
                    </tr></thead>
                    <tbody>
                      <tr v-for="d in selectedInstallation.generation" :key="d.datetime">
                        <td class="fw-semibold">{{ d.datetime }}</td>
                        <td class="small">{{ d.conditions }}</td>
                        <td>{{ d.totalKWh.toFixed(1) }}</td>
                        <td>{{ d.peakKW.toFixed(1) }}</td>
                        <td v-if="selectedInstallation.type === 'solar'" class="small">{{ formatTemp(d.tempmax) }} / {{ formatTemp(d.tempmin) }}</td>
                        <td v-else class="small">{{ formatSpeed(d.windspeed) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Detail: Weather Data -->
          <div v-if="detailView === 'weather'">
            <div v-if="!selectedInstallation.weatherData" class="text-center text-muted py-5">
              <p>No weather data loaded. Click <strong>Refresh</strong> to fetch data.</p>
            </div>
            <template v-else>
              <!-- Weather overview chart -->
              <div class="card mb-3">
                <div class="card-header py-2"><h6 class="mb-0">Weather Conditions</h6></div>
                <div class="card-body p-2">
                  <div ref="weatherChartContainer" class="ed-chart-container"></div>
                </div>
              </div>
              <!-- Hourly data table -->
              <div class="card">
                <div class="card-header py-2 d-flex justify-content-between align-items-center">
                  <h6 class="mb-0">Hourly Weather Data</h6>
                  <span class="badge bg-secondary">{{ weatherHoursDisplay.length }} hours</span>
                </div>
                <div class="card-body p-0">
                  <div class="table-responsive" style="max-height:400px;overflow-y:auto">
                    <table class="table table-sm table-striped mb-0 small">
                      <thead class="sticky-top bg-white"><tr>
                        <th>Time</th><th>Temp</th><th>Cloud %</th><th>Wind</th><th>Precip</th>
                        <th v-if="selectedInstallation.type === 'solar'">GHI</th>
                        <th v-if="selectedInstallation.type === 'solar'">DNI</th>
                        <th>Conditions</th>
                      </tr></thead>
                      <tbody>
                        <tr v-for="h in weatherHoursDisplay" :key="h.datetimeEpoch">
                          <td class="fw-semibold">{{ h.datetime }}</td>
                          <td>{{ formatTemp(h.temp) }}</td>
                          <td>{{ Math.round(h.cloudcover ?? 0) }}%</td>
                          <td>{{ formatSpeed(h.windspeed) }}</td>
                          <td>{{ (h.precip ?? 0).toFixed(1) }}</td>
                          <td v-if="selectedInstallation.type === 'solar'">{{ Math.round(h.ghiradiation ?? h.solarradiation ?? 0) }}</td>
                          <td v-if="selectedInstallation.type === 'solar'">{{ Math.round(h.dniradiation ?? 0) }}</td>
                          <td class="small">{{ h.conditions }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- Detail: Wind Profile (wind only) -->
          <div v-if="detailView === 'windprofile' && selectedInstallation.type === 'wind'">
            <div class="card mb-3">
              <div class="card-header py-2"><h6 class="mb-0">Multi-Altitude Wind Speed Profile</h6></div>
              <div class="card-body p-2">
                <div ref="windProfileChartContainer" class="ed-chart-container"></div>
              </div>
            </div>
            <div class="card">
              <div class="card-header py-2"><h6 class="mb-0">Wind Speeds at Different Heights</h6></div>
              <div class="card-body p-0">
                <div class="table-responsive" style="max-height:400px;overflow-y:auto">
                  <table class="table table-sm table-striped mb-0 small">
                    <thead class="sticky-top bg-white"><tr>
                      <th>Time</th><th>10m (ref)</th><th>40m</th><th>80m</th><th>120m</th><th>160m</th>
                    </tr></thead>
                    <tbody>
                      <tr v-for="wp in selectedInstallation.windProfile" :key="wp.datetimeEpoch">
                        <td class="fw-semibold">{{ wp.datetime }}</td>
                        <td>{{ formatSpeed(wp.baseSpeed) }}</td>
                        <td>{{ formatSpeed(wp[40]) }}</td>
                        <td>{{ formatSpeed(wp[80]) }}</td>
                        <td>{{ formatSpeed(wp[120]) }}</td>
                        <td>{{ formatSpeed(wp[160]) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Detail: Irradiance (solar only) -->
          <div v-if="detailView === 'irradiance' && selectedInstallation.type === 'solar'">
            <div class="card mb-3">
              <div class="card-header py-2"><h6 class="mb-0">Solar Irradiance Components (GHI, DNI, DHI)</h6></div>
              <div class="card-body p-2">
                <div ref="irradianceChartContainer" class="ed-chart-container"></div>
              </div>
            </div>
            <div class="card">
              <div class="card-header py-2"><h6 class="mb-0">Irradiance Data</h6></div>
              <div class="card-body p-0">
                <div class="table-responsive" style="max-height:400px;overflow-y:auto">
                  <table class="table table-sm table-striped mb-0 small">
                    <thead class="sticky-top bg-white"><tr>
                      <th>Time</th><th>GHI (W/m²)</th><th>DNI (W/m²)</th><th>DHI (W/m²)</th><th>GTI (W/m²)</th><th>Sun Elev.</th><th>Cloud %</th>
                    </tr></thead>
                    <tbody>
                      <tr v-for="h in selectedInstallation.hourlyGeneration" :key="h.datetimeEpoch">
                        <td class="fw-semibold">{{ h.datetime }}</td>
                        <td>{{ Math.round(h.ghi) }}</td>
                        <td>{{ Math.round(h.dni) }}</td>
                        <td>{{ Math.round(h.dhi) }}</td>
                        <td>{{ Math.round(h.gti) }}</td>
                        <td>–</td>
                        <td>{{ Math.round(h.cloudcover) }}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- ════════════════ TAB: CHARTS ════════════════ -->
      <div v-show="activeTab === 'charts'">
        <div v-if="!hasRun || !installations.length" class="text-center text-muted py-5">
          <i class="bi bi-bar-chart-line fs-1 d-block mb-2"></i>
          <p>Add installations and click <strong>Refresh</strong> to generate charts.</p>
        </div>
        <template v-else>
          <div class="mb-3">
            <select v-model="chartsInstId" class="form-select form-select-sm" style="width:auto">
              <option v-for="inst in installations" :key="inst.id" :value="inst.id">{{ inst.name }} ({{ inst.type }})</option>
            </select>
          </div>
          <div v-if="chartsInst">
            <div class="row g-3">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header py-2"><h6 class="mb-0">Generation Forecast (kW)</h6></div>
                  <div class="card-body p-2"><div ref="chartsGenContainer" class="ed-chart-container"></div></div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header py-2"><h6 class="mb-0">Risk Breakdown</h6></div>
                  <div class="card-body p-2"><div ref="chartsRiskContainer" class="ed-chart-container"></div></div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>

    <!-- ═══════════════════ ADD/EDIT MODAL ═══════════════════ -->
    <div v-if="showInstModal" class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,0.5)" @click.self="showInstModal = false">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingInst ? 'Edit' : 'Add' }} {{ instForm.type === 'solar' ? 'Solar Panel' : 'Wind Turbine' }}</h5>
            <button type="button" class="btn-close" @click="showInstModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Name</label>
              <input v-model="instForm.name" class="form-control" placeholder="Installation name" />
            </div>
            <div class="row g-3 mb-3">
              <div class="col-6">
                <label class="form-label">Latitude</label>
                <input v-model.number="instForm.lat" type="number" step="0.0001" class="form-control" />
              </div>
              <div class="col-6">
                <label class="form-label">Longitude</label>
                <input v-model.number="instForm.lon" type="number" step="0.0001" class="form-control" />
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Location (address search)</label>
              <div class="input-group">
                <input v-model="instForm.locationSearch" class="form-control" placeholder="Enter address to geocode" @keyup.enter="geocodeInstLocation" />
                <button class="btn btn-outline-primary" @click="geocodeInstLocation" :disabled="!instForm.locationSearch">
                  <i class="bi bi-search"></i>
                </button>
              </div>
            </div>

            <!-- Solar-specific -->
            <template v-if="instForm.type === 'solar'">
              <div class="row g-3 mb-3">
                <div class="col-6">
                  <label class="form-label">Area (m²)</label>
                  <input v-model.number="instForm.areaSqM" type="number" class="form-control" />
                </div>
                <div class="col-6">
                  <label class="form-label">Peak Power (W)</label>
                  <input v-model.number="instForm.peakPowerW" type="number" class="form-control" />
                </div>
              </div>
              <div class="row g-3 mb-3">
                <div class="col-6">
                  <label class="form-label">Tilt Angle (0-90°)</label>
                  <input v-model.number="instForm.tiltAngle" type="number" min="0" max="90" class="form-control" />
                </div>
                <div class="col-6">
                  <label class="form-label">Azimuth (0-360°)</label>
                  <input v-model.number="instForm.azimuth" type="number" min="0" max="360" class="form-control" />
                </div>
              </div>
              <div v-if="instForm.areaSqM > 0 && instForm.peakPowerW > 0" class="alert alert-info small py-2">
                <strong>Efficiency:</strong> {{ ((instForm.peakPowerW / (instForm.areaSqM * 1000)) * 100).toFixed(1) }}%
                &middot; <strong>Power Density:</strong> {{ (instForm.peakPowerW / instForm.areaSqM).toFixed(0) }} W/m²
              </div>
            </template>

            <!-- Wind-specific -->
            <template v-if="instForm.type === 'wind'">
              <div class="row g-3 mb-3">
                <div class="col-6">
                  <label class="form-label">Hub Height (m)</label>
                  <input v-model.number="instForm.hubHeightM" type="number" class="form-control" />
                </div>
                <div class="col-6">
                  <label class="form-label">Rated Power (kW)</label>
                  <input v-model.number="instForm.ratedPowerKW" type="number" class="form-control" />
                </div>
              </div>
              <div class="alert alert-info small py-2">
                <strong>Power Curve:</strong> Cut-in 3 m/s &middot; Rated 15 m/s &middot; Cut-out 25 m/s
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showInstModal = false">Cancel</button>
            <button class="btn btn-primary" @click="saveInstallation">
              <i class="bi bi-check me-1"></i>{{ editingInst ? 'Update' : 'Add' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════════════ CONFIRM REMOVE MODAL ═══════════════════ -->
    <div v-if="showRemoveConfirm" class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,0.5)" @click.self="showRemoveConfirm = false">
      <div class="modal-dialog modal-sm">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Remove Installation</h5>
            <button type="button" class="btn-close" @click="showRemoveConfirm = false"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to remove <strong>{{ removeTarget?.name }}</strong>?</p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary btn-sm" @click="showRemoveConfirm = false">Cancel</button>
            <button class="btn btn-danger btn-sm" @click="doRemoveInstallation">Remove</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { VcGlobal } from '/src/visualcrossing-core/vcglobal_conn.js';
import { getServerRoot } from '../weatherexceptions/apiConfig.js';
import '/src/weather-map/WxTileLayer.js';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { patchLeaflet } from '/src/utils/leaflet-patches.js';
patchLeaflet();
import { onMounted } from 'vue';
import { useEnergyDashboardEngine } from './useEnergyDashboardEngine.js';

export default {
  name: 'EnergyDashboard',

  props: {
    serverRoot: { type: String, default: null },
  },

  setup(props) {
    const engine = useEnergyDashboardEngine({
      serverRoot: props.serverRoot,
    });

    onMounted(() => {
      engine.initSession();
      engine.loadInstallationsFromStorage();
    });

    return { ...engine };
  },

  data() {
    return {
      darkMode: false,
      activeTab: 'dashboard',
      detailView: 'risk',
      showAllAttention: false,
      selectedInstallation: null,
      lastUpdateTime: null,

      // Map
      leafletMap: null,
      mapMarkers: [],

      // Modals
      showInstModal: false,
      editingInst: null,
      instForm: this._defaultInstForm('solar'),
      showRemoveConfirm: false,
      removeTarget: null,

      // Charts tab
      chartsInstId: null,

      tabs: [
        { id: 'dashboard', label: 'Dashboard', icon: 'bi bi-speedometer2' },
        { id: 'solar', label: 'Solar Panels', icon: 'bi bi-sun' },
        { id: 'wind', label: 'Wind Turbines', icon: 'bi bi-wind' },
        { id: 'detail', label: 'Detail View', icon: 'bi bi-graph-up' },
        { id: 'charts', label: 'Charts', icon: 'bi bi-bar-chart-line' },
      ],
    };
  },

  computed: {
    lastUpdateLabel() {
      if (!this.lastUpdateTime) return '';
      const diff = Math.round((Date.now() - this.lastUpdateTime) / 1000);
      if (diff < 60) return `${diff}s ago`;
      if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
      return new Date(this.lastUpdateTime).toLocaleTimeString();
    },

    weatherHoursDisplay() {
      if (!this.selectedInstallation?.weatherData?.days) return [];
      return this.selectedInstallation.weatherData.days.flatMap(d => d.hours || []);
    },

    chartsInst() {
      if (!this.chartsInstId) return null;
      return this.installations.find(i => i.id === this.chartsInstId) || null;
    },
  },

  watch: {
    hasSession(val) {
      if (val) this.$nextTick(() => this.initMap());
    },
    activeTab(tab) {
      if (tab === 'dashboard') this.$nextTick(() => this.initMap());
      if (tab === 'charts' && this.chartsInst) this.$nextTick(() => this.renderChartsTab());
    },
    selectedInstallation(inst) {
      if (inst && this.activeTab === 'detail') {
        this.$nextTick(() => this.renderDetailCharts());
      }
    },
    detailView() {
      this.$nextTick(() => this.renderDetailCharts());
    },
    chartsInstId() {
      this.$nextTick(() => this.renderChartsTab());
    },
  },

  mounted() {
    this.$nextTick(() => this.initMap());
    if (this.installations.length === 0) {
      this.addDemoInstallations();
    }
  },

  methods: {
    _defaultInstForm(type) {
      return {
        type: type || 'solar',
        name: '',
        lat: 40.7128,
        lon: -74.006,
        locationSearch: '',
        areaSqM: 100,
        peakPowerW: 20000,
        tiltAngle: 30,
        azimuth: 180,
        hubHeightM: 80,
        ratedPowerKW: 2000,
      };
    },

    addDemoInstallations() {
      if (this.installations.length > 0) return;
      this.addInstallation({ name: 'Phoenix Solar Farm', type: 'solar', lat: 33.4484, lon: -112.0740, areaSqM: 500, peakPowerW: 100000, tiltAngle: 25, azimuth: 180 });
      this.addInstallation({ name: 'Denver Solar Array', type: 'solar', lat: 39.7392, lon: -104.9903, areaSqM: 200, peakPowerW: 40000, tiltAngle: 35, azimuth: 180 });
      this.addInstallation({ name: 'Iowa Wind Farm', type: 'wind', lat: 41.8780, lon: -93.0977, hubHeightM: 120, ratedPowerKW: 3000 });
      this.addInstallation({ name: 'Texas Wind Park', type: 'wind', lat: 32.3513, lon: -100.4465, hubHeightM: 80, ratedPowerKW: 2000 });
      this.saveInstallationsToStorage();
    },

    async refreshData() {
      await this.fetchAllInstallations();
      this.lastUpdateTime = Date.now();
      this.saveInstallationsToStorage();
      this.$nextTick(() => {
        this.updateMapMarkers();
        if (this.selectedInstallation) this.renderDetailCharts();
        if (this.activeTab === 'charts' && this.chartsInst) this.renderChartsTab();
      });
    },

    selectInstallation(inst) {
      this.selectedInstallation = inst;
      this.activeTab = 'detail';
      this.detailView = 'risk';
    },

    // ─── Map ──────────────────────────────────────────────
    initMap() {
      if (this.leafletMap) {
        this.leafletMap.invalidateSize();
        this.updateMapMarkers();
        return;
      }
      const container = this.$refs.mapContainer;
      if (!container) return;

      this.leafletMap = L.map(container, {
        center: [39.8, -98.5],
        zoom: 4,
        zoomControl: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 18,
      }).addTo(this.leafletMap);

      // Add weather tile layer if available
      try {
        if (typeof WxTileLayer !== 'undefined' && VcGlobal.MAP_TILE_KEY) {
          const wxLayer = new WxTileLayer({
            key: VcGlobal.MAP_TILE_KEY,
            element: 'temp',
            opacity: 0.4,
          });
          wxLayer.addTo(this.leafletMap);
        }
      } catch (e) {
        // Weather tile layer not available
      }

      this.updateMapMarkers();
    },

    updateMapMarkers() {
      if (!this.leafletMap) return;

      // Clear existing markers
      this.mapMarkers.forEach(m => this.leafletMap.removeLayer(m));
      this.mapMarkers = [];

      if (!this.installations.length) return;

      const bounds = L.latLngBounds([]);

      for (const inst of this.installations) {
        const color = inst.riskLevel ? this.riskColor(inst.riskLevel) : '#6b7280';
        const label = inst.type === 'solar' ? 'SP' : 'WT';

        const icon = L.divIcon({
          className: 'ed-map-marker',
          html: `<div style="background:${color};color:white;width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:10px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)">${label}</div>`,
          iconSize: [30, 30],
          iconAnchor: [15, 15],
        });

        const marker = L.marker([inst.lat, inst.lon], { icon })
          .addTo(this.leafletMap)
          .bindPopup(`
            <strong>${inst.name}</strong><br/>
            Type: ${inst.type === 'solar' ? 'Solar Panel' : 'Wind Turbine'}<br/>
            Risk: ${inst.riskScore != null ? inst.riskScore + '/100 (' + inst.riskLevel + ')' : 'N/A'}<br/>
            ${inst.type === 'solar' ? 'Area: ' + inst.areaSqM + ' m² | Peak: ' + (inst.peakPowerW / 1000).toFixed(1) + ' kW' : 'Hub: ' + inst.hubHeightM + 'm | Rated: ' + inst.ratedPowerKW + ' kW'}
          `)
          .on('click', () => this.selectInstallation(inst));

        this.mapMarkers.push(marker);
        bounds.extend([inst.lat, inst.lon]);
      }

      if (bounds.isValid()) {
        this.leafletMap.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
      }
    },

    flyToInstallation(id) {
      if (!this.leafletMap) return;
      if (!id) {
        // Fit all
        const bounds = L.latLngBounds(this.installations.map(i => [i.lat, i.lon]));
        if (bounds.isValid()) this.leafletMap.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
        return;
      }
      const inst = this.installations.find(i => i.id === id);
      if (inst) this.leafletMap.flyTo([inst.lat, inst.lon], 10, { duration: 1.5 });
    },

    // ─── Installation CRUD ────────────────────────────────
    openAddModal(type) {
      this.editingInst = null;
      this.instForm = this._defaultInstForm(type);
      this.showInstModal = true;
    },

    openEditModal(inst) {
      this.editingInst = inst;
      this.instForm = {
        type: inst.type,
        name: inst.name,
        lat: inst.lat,
        lon: inst.lon,
        locationSearch: '',
        areaSqM: inst.areaSqM,
        peakPowerW: inst.peakPowerW,
        tiltAngle: inst.tiltAngle,
        azimuth: inst.azimuth,
        hubHeightM: inst.hubHeightM,
        ratedPowerKW: inst.ratedPowerKW,
      };
      this.showInstModal = true;
    },

    saveInstallation() {
      if (this.editingInst) {
        this.updateInstallation(this.editingInst.id, {
          name: this.instForm.name,
          lat: this.instForm.lat,
          lon: this.instForm.lon,
          areaSqM: this.instForm.areaSqM,
          peakPowerW: this.instForm.peakPowerW,
          tiltAngle: this.instForm.tiltAngle,
          azimuth: this.instForm.azimuth,
          hubHeightM: this.instForm.hubHeightM,
          ratedPowerKW: this.instForm.ratedPowerKW,
        });
      } else {
        this.addInstallation({ ...this.instForm });
      }
      this.saveInstallationsToStorage();
      this.showInstModal = false;
      this.updateMapMarkers();
    },

    confirmRemove(inst) {
      this.removeTarget = inst;
      this.showRemoveConfirm = true;
    },

    doRemoveInstallation() {
      if (this.removeTarget) {
        this.removeInstallation(this.removeTarget.id);
        if (this.selectedInstallation?.id === this.removeTarget.id) {
          this.selectedInstallation = null;
        }
        this.saveInstallationsToStorage();
        this.updateMapMarkers();
      }
      this.showRemoveConfirm = false;
      this.removeTarget = null;
    },

    async geocodeInstLocation() {
      const query = this.instForm.locationSearch?.trim();
      if (!query) return;
      try {
        const baseUrl = getServerRoot(this.serverRoot);
        const url = `${baseUrl}/VisualCrossingWebServices/rest/services/timeline/${encodeURIComponent(query)}/today?key=${this.apiKey}&include=current&elements=datetime`;
        const resp = await fetch(url);
        if (resp.ok) {
          const data = await resp.json();
          if (data.latitude != null && data.longitude != null) {
            this.instForm.lat = data.latitude;
            this.instForm.lon = data.longitude;
            if (!this.instForm.name) {
              this.instForm.name = data.resolvedAddress || query;
            }
          }
        }
      } catch (e) {
        console.warn('Geocode failed:', e);
      }
    },

    // ─── Risk Helpers ─────────────────────────────────────
    riskBadgeClass(level) {
      switch (level) {
        case 'extreme': return 'bg-danger';
        case 'high':    return 'bg-warning text-dark';
        case 'moderate': return 'bg-warning text-dark';
        default:        return 'bg-success';
      }
    },

    // ─── D3 Charts ────────────────────────────────────────
    renderDetailCharts() {
      const inst = this.selectedInstallation;
      if (!inst || inst.status !== 'ok') return;

      if (this.detailView === 'generation') this.renderGenerationChart(inst);
      if (this.detailView === 'weather') this.renderWeatherChart(inst);
      if (this.detailView === 'windprofile' && inst.type === 'wind') this.renderWindProfileChart(inst);
      if (this.detailView === 'irradiance' && inst.type === 'solar') this.renderIrradianceChart(inst);
    },

    renderGenerationChart(inst) {
      const container = this.$refs.genChartContainer;
      if (!container) return;
      container.innerHTML = '';

      const data = inst.hourlyGeneration;
      if (!data?.length) return;

      const margin = { top: 20, right: 20, bottom: 40, left: 50 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 200;

      const svg = d3.select(container).append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      const x = d3.scaleLinear().domain([0, data.length - 1]).range([0, width]);
      const y = d3.scaleLinear().domain([0, d3.max(data, d => d.genKW) * 1.1 || 1]).range([height, 0]);

      // Area fill
      const area = d3.area()
        .x((d, i) => x(i))
        .y0(height)
        .y1(d => y(d.genKW))
        .curve(d3.curveMonotoneX);

      svg.append('path').datum(data)
        .attr('fill', inst.type === 'solar' ? 'rgba(251,191,36,0.3)' : 'rgba(59,130,246,0.3)')
        .attr('d', area);

      // Line
      const line = d3.line()
        .x((d, i) => x(i))
        .y(d => y(d.genKW))
        .curve(d3.curveMonotoneX);

      svg.append('path').datum(data)
        .attr('fill', 'none')
        .attr('stroke', inst.type === 'solar' ? '#f59e0b' : '#3b82f6')
        .attr('stroke-width', 2)
        .attr('d', line);

      // Axes
      const tickInterval = Math.max(1, Math.floor(data.length / 12));
      svg.append('g').attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickValues(d3.range(0, data.length, tickInterval)).tickFormat(i => data[i]?.datetime?.slice(11, 16) || ''));
      svg.append('g').call(d3.axisLeft(y).ticks(5).tickFormat(d => d.toFixed(0)));

      // Labels
      svg.append('text').attr('x', width / 2).attr('y', height + 35)
        .attr('text-anchor', 'middle').attr('class', 'ed-chart-label').text('Time');
      svg.append('text').attr('transform', 'rotate(-90)').attr('y', -40).attr('x', -height / 2)
        .attr('text-anchor', 'middle').attr('class', 'ed-chart-label').text('Power (kW)');
    },

    renderWeatherChart(inst) {
      const container = this.$refs.weatherChartContainer;
      if (!container || !inst.weatherData?.days) return;
      container.innerHTML = '';

      const hours = inst.weatherData.days.flatMap(d => d.hours || []);
      if (!hours.length) return;

      const margin = { top: 20, right: 50, bottom: 40, left: 50 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 200;

      const svg = d3.select(container).append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      const x = d3.scaleLinear().domain([0, hours.length - 1]).range([0, width]);
      const yTemp = d3.scaleLinear().domain(d3.extent(hours, d => d.temp)).nice().range([height, 0]);
      const yCloud = d3.scaleLinear().domain([0, 100]).range([height, 0]);

      // Cloud cover area
      const cloudArea = d3.area()
        .x((d, i) => x(i)).y0(height).y1(d => yCloud(d.cloudcover ?? 0))
        .curve(d3.curveMonotoneX);
      svg.append('path').datum(hours)
        .attr('fill', 'rgba(148,163,184,0.2)').attr('d', cloudArea);

      // Temperature line
      const tempLine = d3.line()
        .x((d, i) => x(i)).y(d => yTemp(d.temp))
        .curve(d3.curveMonotoneX);
      svg.append('path').datum(hours)
        .attr('fill', 'none').attr('stroke', '#ef4444').attr('stroke-width', 2).attr('d', tempLine);

      // Axes
      const tickInterval = Math.max(1, Math.floor(hours.length / 12));
      svg.append('g').attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickValues(d3.range(0, hours.length, tickInterval)).tickFormat(i => hours[i]?.datetime?.slice(11, 16) || ''));
      svg.append('g').call(d3.axisLeft(yTemp).ticks(5));
      svg.append('g').attr('transform', `translate(${width},0)`)
        .call(d3.axisRight(yCloud).ticks(5).tickFormat(d => d + '%'));

      // Legend
      const legend = svg.append('g').attr('transform', `translate(10, 0)`);
      legend.append('line').attr('x1', 0).attr('x2', 15).attr('y1', 0).attr('y2', 0).attr('stroke', '#ef4444').attr('stroke-width', 2);
      legend.append('text').attr('x', 20).attr('y', 4).text('Temp').attr('class', 'ed-chart-legend-text');
      legend.append('rect').attr('x', 70).attr('y', -5).attr('width', 10).attr('height', 10).attr('fill', 'rgba(148,163,184,0.4)');
      legend.append('text').attr('x', 85).attr('y', 4).text('Cloud %').attr('class', 'ed-chart-legend-text');
    },

    renderWindProfileChart(inst) {
      const container = this.$refs.windProfileChartContainer;
      if (!container || !inst.windProfile?.length) return;
      container.innerHTML = '';

      const data = inst.windProfile;
      const margin = { top: 20, right: 20, bottom: 40, left: 50 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 220;

      const svg = d3.select(container).append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      const x = d3.scaleLinear().domain([0, data.length - 1]).range([0, width]);
      const maxWS = d3.max(data, d => Math.max(d[40] || 0, d[80] || 0, d[120] || 0, d[160] || 0));
      const y = d3.scaleLinear().domain([0, maxWS * 1.1 || 1]).range([height, 0]);

      const altColors = { 40: '#a855f7', 80: '#3b82f6', 120: '#10b981', 160: '#f59e0b' };

      for (const alt of [40, 80, 120, 160]) {
        const line = d3.line().x((d, i) => x(i)).y(d => y(d[alt] || 0)).curve(d3.curveMonotoneX);
        svg.append('path').datum(data)
          .attr('fill', 'none').attr('stroke', altColors[alt]).attr('stroke-width', alt === inst.hubHeightM ? 3 : 1.5)
          .attr('d', line);
      }

      const tickInterval = Math.max(1, Math.floor(data.length / 12));
      svg.append('g').attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickValues(d3.range(0, data.length, tickInterval)).tickFormat(i => data[i]?.datetime?.slice(11, 16) || ''));
      svg.append('g').call(d3.axisLeft(y).ticks(5));

      // Legend
      const legend = svg.append('g').attr('transform', `translate(${width - 200}, 0)`);
      [40, 80, 120, 160].forEach((alt, i) => {
        legend.append('line').attr('x1', 0).attr('x2', 15).attr('y1', i * 16).attr('y2', i * 16)
          .attr('stroke', altColors[alt]).attr('stroke-width', alt === inst.hubHeightM ? 3 : 1.5);
        legend.append('text').attr('x', 20).attr('y', i * 16 + 4)
          .text(`${alt}m${alt === inst.hubHeightM ? ' (hub)' : ''}`).attr('class', 'ed-chart-legend-text');
      });
    },

    renderIrradianceChart(inst) {
      const container = this.$refs.irradianceChartContainer;
      if (!container || !inst.hourlyGeneration?.length) return;
      container.innerHTML = '';

      const data = inst.hourlyGeneration;
      const margin = { top: 20, right: 20, bottom: 40, left: 50 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 220;

      const svg = d3.select(container).append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      const x = d3.scaleLinear().domain([0, data.length - 1]).range([0, width]);
      const maxIrr = d3.max(data, d => Math.max(d.ghi || 0, d.dni || 0, d.dhi || 0));
      const y = d3.scaleLinear().domain([0, maxIrr * 1.1 || 1]).range([height, 0]);

      const series = [
        { key: 'ghi', color: '#f59e0b', label: 'GHI' },
        { key: 'dni', color: '#ef4444', label: 'DNI' },
        { key: 'dhi', color: '#3b82f6', label: 'DHI' },
      ];

      for (const s of series) {
        const line = d3.line().x((d, i) => x(i)).y(d => y(d[s.key] || 0)).curve(d3.curveMonotoneX);
        svg.append('path').datum(data)
          .attr('fill', 'none').attr('stroke', s.color).attr('stroke-width', 2).attr('d', line);
      }

      const tickInterval = Math.max(1, Math.floor(data.length / 12));
      svg.append('g').attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickValues(d3.range(0, data.length, tickInterval)).tickFormat(i => data[i]?.datetime?.slice(11, 16) || ''));
      svg.append('g').call(d3.axisLeft(y).ticks(5));

      svg.append('text').attr('x', width / 2).attr('y', height + 35)
        .attr('text-anchor', 'middle').attr('class', 'ed-chart-label').text('Time');
      svg.append('text').attr('transform', 'rotate(-90)').attr('y', -40).attr('x', -height / 2)
        .attr('text-anchor', 'middle').attr('class', 'ed-chart-label').text('W/m²');

      // Legend
      const legend = svg.append('g').attr('transform', `translate(${width - 160}, 0)`);
      series.forEach((s, i) => {
        legend.append('line').attr('x1', 0).attr('x2', 15).attr('y1', i * 16).attr('y2', i * 16).attr('stroke', s.color).attr('stroke-width', 2);
        legend.append('text').attr('x', 20).attr('y', i * 16 + 4).text(s.label).attr('class', 'ed-chart-legend-text');
      });
    },

    renderChartsTab() {
      const inst = this.chartsInst;
      if (!inst || inst.status !== 'ok') return;

      // Generation chart
      this.renderChartsGeneration(inst);
      this.renderChartsRisk(inst);
    },

    renderChartsGeneration(inst) {
      const container = this.$refs.chartsGenContainer;
      if (!container) return;
      container.innerHTML = '';

      const data = inst.hourlyGeneration;
      if (!data?.length) return;

      const margin = { top: 20, right: 20, bottom: 40, left: 50 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 200;

      const svg = d3.select(container).append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      const x = d3.scaleBand().domain(data.map((_, i) => i)).range([0, width]).padding(0.1);
      const y = d3.scaleLinear().domain([0, d3.max(data, d => d.genKW) * 1.1 || 1]).range([height, 0]);

      // Bars
      svg.selectAll('rect').data(data).join('rect')
        .attr('x', (d, i) => x(i))
        .attr('y', d => y(d.genKW))
        .attr('width', x.bandwidth())
        .attr('height', d => height - y(d.genKW))
        .attr('fill', inst.type === 'solar' ? '#fbbf24' : '#60a5fa')
        .attr('rx', 1);

      // Trend line
      const line = d3.line().x((d, i) => x(i) + x.bandwidth() / 2).y(d => y(d.genKW)).curve(d3.curveMonotoneX);
      svg.append('path').datum(data)
        .attr('fill', 'none').attr('stroke', '#1f2937').attr('stroke-width', 1.5).attr('stroke-dasharray', '4,2').attr('d', line);

      const tickInterval = Math.max(1, Math.floor(data.length / 8));
      svg.append('g').attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickValues(d3.range(0, data.length, tickInterval)).tickFormat(i => data[i]?.datetime?.slice(11, 16) || ''));
      svg.append('g').call(d3.axisLeft(y).ticks(5));
    },

    renderChartsRisk(inst) {
      const container = this.$refs.chartsRiskContainer;
      if (!container || !inst.riskFactors?.length) return;
      container.innerHTML = '';

      const data = inst.riskFactors;
      const margin = { top: 20, right: 20, bottom: 20, left: 120 };
      const barHeight = 24;
      const height = data.length * barHeight;
      const width = container.clientWidth - margin.left - margin.right;

      const svg = d3.select(container).append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      const x = d3.scaleLinear().domain([0, 100]).range([0, width]);
      const y = d3.scaleBand().domain(data.map(d => d.name)).range([0, height]).padding(0.2);

      svg.selectAll('rect').data(data).join('rect')
        .attr('x', 0).attr('y', d => y(d.name))
        .attr('width', d => x(d.score))
        .attr('height', y.bandwidth())
        .attr('fill', d => this.riskColor(d.level))
        .attr('rx', 3);

      svg.selectAll('.score-label').data(data).join('text')
        .attr('x', d => x(d.score) + 5).attr('y', d => y(d.name) + y.bandwidth() / 2 + 4)
        .text(d => d.score).attr('class', 'ed-chart-legend-text');

      svg.append('g').call(d3.axisLeft(y).tickSize(0).tickPadding(8));
    },
  },
};
</script>

<style src="./energy-dashboard.scss"></style>

<style scoped>
/* Spin animation for refresh icon */
.spin-icon {
  animation: ed-spin 1s linear infinite;
}
@keyframes ed-spin {
  to { transform: rotate(360deg); }
}

.cursor-pointer { cursor: pointer; }
</style>
